import { excludeInternalSupportEmployees } from '../../../utils/employeeVisibility';
import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, Box } from '@mui/material';
import ReportToolbar from "../ReportToolbar";
import ReportFooter from "../ReportFooter";
import { ProgramFilter, ReportFilterBar } from "../ReportFilters";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import dayjs from "dayjs";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
];

export default function HomeVisitsReport(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const { startDate, endDate, setStartDate, setEndDate } = props;
    const apiRef = useGridApiRef();

    useEffect(() => {
        fetchData();
        fetchEmployeeData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);

        axios
            .get(APIURL + "/reports/getHomeVisitsReport", {
                params: {
                    compID: props.user.comp_id,
                    empID: props.user.emp_id,
                    empType: props.user.type,
                    selectedEmpID: selectedEmployee,
                    custID: selectedCustomer?.cus_id,
                    start: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    end: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data.length !== 0 ? response?.data : []);
            })
            .catch((err) => {
                console.log(err);
            });

        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            })
            .catch((err) => {
                console.log(err);
            });

        setIsLoading(false);
    };

    const fetchEmployeeData = async () => {
        if (props.user.type === 'Admin') {
            axios
                .get(APIURL + '/dropdowns/dropdownType/employeeslist', {
                    params: {
                        comp_id: props.user.comp_id
                    }
                })
                .then((response) => {
                    const empList = excludeInternalSupportEmployees(response.data).map((emp) => { return { emp_id: emp.id, emp_name: emp.employee_name } });
                    setEmployeeList(empList);
                });
        } else if (props.user.type.includes('Manager')) {
            axios
                .get(APIURL + "/employees/v2/associated", {
                    params: {
                        company_id: props.user.comp_id,
                        employee_id: props.user.emp_id
                    },
                })
                .then((response) => {
                    let associatedEmployees = response.data.directlyManaged.concat(response.data.hr_managed).concat({ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support });

                    associatedEmployees = excludeInternalSupportEmployees(associatedEmployees).filter((obj, index, self) => {
                        return index === self.findIndex((t) => (
                            t.emp_id === obj.emp_id
                        ));
                    }).map((emp) => { return { emp_id: emp.emp_id, emp_name: emp.emp_name ? emp.emp_name : emp.emp_first_name + ' ' + emp.emp_last_name } });

                    setEmployeeList(associatedEmployees);
                });
        } else {
            setEmployeeList(excludeInternalSupportEmployees([{ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support }]));
        }
    };

    const rows = data.map((item, idx) => {
        return {
            id: idx,
            emp_name: item.emp_first_name + ' ' + item.emp_last_name,
            cus_name: item.cus_name,
            tas_id: item.tas_id,
            session_id: item.session_id,
            schedule_name: item.schedule_name,
            shift_date: moment.utc(item.date).format('MM/DD/YYYY'),
            task_type: item.task_type,
        };
    });

    const columns = [
        { field: "cus_name", headerName: "Client Name", width: 250 },
        { field: "schedule_name", headerName: "Shift Name", width: 250 },
        { field: "shift_date", headerName: "Shift Date", width: 150 },
        { field: "task_type", headerName: "Shift Type", width: 150 },
    ];

    const CustomToolbar = () => {
        return (
            <ReportToolbar companyName={props.user.company_name} reportTitle="Home Visits Report">
                <Box sx={{ pl: 1, pr: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Date Range: {moment(startDate).format('MM/DD/YYYY')} - {moment(endDate).format('MM/DD/YYYY')}
                    </div>
                </Box>
            </ReportToolbar>
        );
    };

    return (
        <div style={{ width: '100%', padding: 10 }}>
            <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between' }}>
                <ReportFilterBar onSearch={() => fetchData()} style={{ flexWrap: 'wrap', rowGap: 10 }}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: "From Date", end: "To Date" }}
                    >
                        <DateRangePicker
                            label='Date Range'
                            value={[
                                startDate ? dayjs(startDate) : null,
                                endDate ? dayjs(endDate) : null,
                            ]}
                            onChange={(newValue) => {
                                setStartDate(newValue[0] ? new Date(newValue[0]) : null);
                                setEndDate(newValue[1] ? new Date(newValue[1]) : null);
                            }}
                            slotProps={{
                                shortcuts: {
                                    items: shortcutsItems,
                                },
                                textField: { size: 'small', style: { width: 275 } }
                            }}
                        />
                    </LocalizationProvider>

                    <Autocomplete
                        id="employee-select"
                        options={employeeList}
                        getOptionLabel={(option) => option.emp_name}
                        onChange={(event, value) => setSelectedEmployee(value?.emp_id ? value.emp_id : null)}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Employee" />}
                        sx={{ width: 250 }}
                        size="small"
                    />

                    <Autocomplete
                        id="client-select"
                        options={customerList}
                        getOptionLabel={(option) => option.cus_name}
                        onChange={(event, value) => setSelectedCustomer(value)}
                        renderOption={(props, option) => (
                            <li {...props} key={option.cus_id}>
                                {option.cus_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Client" />}
                        sx={{ width: 250 }}
                        size="small"
                    />

                    <ProgramFilter
                        comp_id={props.user.comp_id}
                        value={selectedProgram}
                        onChange={setSelectedProgram}
                    />
                </ReportFilterBar>
            </div>
            <div style={{ height: 'calc(100vh - 250px)' }}>
                <DataGridPro
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    showToolbar
                    slots={{
                        toolbar: CustomToolbar,
                        footer: ReportFooter
                    }}
                    loading={isLoading}
                    treeData
                    getTreeDataPath={(row) => [row.emp_name, `(${row.id}) - ` + (row.tas_id ? `Task: ${row.tas_id}` : `Shift: ${row.session_id}`)]}
                    groupingColDef={{ headerName: 'Employee' }}
                    defaultGroupingExpansionDepth={-1}
                    getRowClassName={(params) => {
                        const node = apiRef.current?.getRowNode?.(params.id);
                        if (node && node.type === 'group') return 'fw-parent-row';
                        const siblings = rows.filter(r => r.emp_name === params.row.emp_name);
                        const childIndex = siblings.findIndex(r => r.id === params.row.id);
                        return childIndex % 2 === 0 ? 'fw-child-row-even' : 'fw-child-row-odd';
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                        '& .MuiDataGrid-row[data-rowindex] .MuiDataGrid-cell': {},
                        '& .fw-parent-row': {
                            backgroundColor: '#bdbdbd',
                            '&:hover': { backgroundColor: '#bdbdbd' },
                        },
                        '& .fw-child-row-even': {
                            backgroundColor: '#ffffff',
                            '&:hover': { backgroundColor: '#ffffff' },
                        },
                        '& .fw-child-row-odd': {
                            backgroundColor: '#eeeeee',
                            '&:hover': { backgroundColor: '#eeeeee' },
                        },
                    }}
                />
            </div>
        </div>
    );
}